import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ReceiptText, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { API, fmtErr, money, fmtDate } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { shopLogoUrl } from "../../lib/branding";
import { printDoc } from "../../lib/printDoc";
import { ReceiptDoc } from "../../components/docs";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

export default function Repayments() {
  const { t } = useLang();
  const [payments, setPayments] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [printMode, setPrintMode] = useState("a4");
  const [draftMark, setDraftMark] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    API.get("/shop/repayments").then((r) => setPayments(r.data)).catch(() => setPayments([]));
  }, []);

  useEffect(() => {
    const openId = params.get("open");
    if (openId) {
      API.get(`/shop/repayments/${openId}/receipt`)
        .then((r) => { setReceipt(r.data); setParams({}); })
        .catch(() => {});
    }
    // eslint-disable-next-line
  }, [params]);

  if (!payments) return <p className="text-slate-500">{t("common.loading")}</p>;

  const total = payments.reduce((s, p) => s + p.amount, 0);

  const openReceipt = async (p) => {
    try {
      const { data } = await API.get(`/shop/repayments/${p.id}/receipt`);
      setReceipt(data);
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  return (
    <div data-testid="repayments-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900">{t("repay.title")}</h1>
        <div className="text-sm text-slate-500">
          {t("reports.total_collected")}: <span className="font-mono font-bold text-emerald-700" data-testid="repayments-total">{money(total)}</span>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("common.date")}</TableHead>
              <TableHead>{t("loans.number")}</TableHead>
              <TableHead>{t("repay.items")}</TableHead>
              <TableHead>{t("loans.customer")}</TableHead>
              <TableHead>{t("repay.interest_part")}</TableHead>
              <TableHead>{t("repay.principal_part")}</TableHead>
              <TableHead>{t("fin.penalty")}</TableHead>
              <TableHead>{t("common.amount")}</TableHead>
              <TableHead>{t("common.method")}</TableHead>
              <TableHead>{t("repay.by")}</TableHead>
              <TableHead className="text-right">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.length === 0 && (
              <TableRow><TableCell colSpan={10} className="text-center text-slate-500 py-10">{t("common.none")}</TableCell></TableRow>
            )}
            {payments.map((p) => (
              <React.Fragment key={p.id}>
                <TableRow data-testid={`repayment-row-${p.id.slice(0, 8)}`}>
                  <TableCell>{fmtDate(p.date)}</TableCell>
                  <TableCell className="font-mono font-semibold">{p.loan_number}</TableCell>
                  <TableCell>
                    {p.items?.length ? (
                      <button data-testid={`repay-item-toggle-${p.id.slice(0, 8)}`}
                        onClick={() => setExpanded(expanded === p.id ? null : p.id)}
                        className="flex items-center gap-1 text-sm font-medium text-emerald-700 hover:text-emerald-800">
                        {p.items[0].name}{p.items.length > 1 ? ` +${p.items.length - 1}` : ""}
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded === p.id ? "rotate-180" : ""}`} />
                      </button>
                    ) : "—"}
                  </TableCell>
                  <TableCell>{p.customer_name}</TableCell>
                  <TableCell className="font-mono text-amber-700">{money(p.interest_portion)}</TableCell>
                  <TableCell className="font-mono">{money(p.principal_portion)}</TableCell>
                  <TableCell className="font-mono text-red-600">{p.penalty_portion ? money(p.penalty_portion) : "—"}</TableCell>
                  <TableCell className="font-mono font-semibold text-emerald-700">{money(p.amount)}</TableCell>
                  <TableCell className="capitalize">{p.method}</TableCell>
                  <TableCell className="text-slate-500 text-sm">{p.created_by}</TableCell>
                  <TableCell className="text-right">
                    <button data-testid={`receipt-button-${p.id.slice(0, 8)}`} onClick={() => openReceipt(p)}
                      className="p-2 hover:bg-slate-100 rounded-lg" title={t("repay.receipt")}>
                      <ReceiptText className="h-4 w-4 text-slate-600" />
                    </button>
                  </TableCell>
                </TableRow>
                {expanded === p.id && (
                  <TableRow data-testid={`repay-item-detail-${p.id.slice(0, 8)}`}>
                    <TableCell colSpan={10} className="bg-slate-50">
                      <div className="flex flex-wrap gap-3 py-1">
                        {p.items.map((it) => (
                          <div key={it.id} data-testid={`repay-item-card-${it.id.slice(0, 8)}`}
                            className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-3 py-2">
                            {it.photo_path && (
                              <img src={`${API.defaults.baseURL}/shop/items/${it.id}/photo`} alt=""
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                                className="h-10 w-10 rounded object-cover border border-slate-200" />
                            )}
                            <div>
                              <div className="text-sm font-medium text-slate-900">{it.name}</div>
                              <div className="text-xs text-slate-500">
                                {it.category}{it.plate_no || it.serial_no ? ` · ${it.plate_no || it.serial_no}` : ""}
                              </div>
                              <div className="text-xs font-mono text-slate-600">
                                {money(it.estimated_value)}{it.location ? ` · ${it.location}` : ""}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!receipt} onOpenChange={(o) => !o && setReceipt(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="receipt-dialog">
          {receipt && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">{t("repay.receipt_title")}</DialogTitle>
              </DialogHeader>
              <ReceiptDoc receipt={receipt} printMode={printMode} draftMark={draftMark} />
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <div className="flex rounded-lg border border-slate-300 overflow-hidden text-xs">
                  <button data-testid="print-mode-a4" onClick={() => setPrintMode("a4")}
                    className={`px-3 py-1.5 ${printMode === "a4" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>{t("print.mode_a4")}</button>
                  <button data-testid="print-mode-thermal" onClick={() => setPrintMode("thermal")}
                    className={`px-3 py-1.5 ${printMode === "thermal" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>{t("print.mode_thermal")}</button>
                </div>
                <button data-testid="print-draft-toggle" onClick={() => setDraftMark(!draftMark)}
                  className={`px-3 py-1.5 rounded-lg border text-xs ${draftMark ? "border-red-400 bg-red-50 text-red-700" : "border-slate-300 text-slate-600"}`}>
                  {t("print.draft")}
                </button>
                <Button data-testid="receipt-print-button"
                  onClick={() => printDoc(document.querySelector('[data-testid="receipt-body"]'), { mode: printMode, draft: draftMark, title: receipt.receipt_no })}
                  className="flex-1 bg-slate-900 hover:bg-slate-800 text-white">
                  {t("repay.print")}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
